import React, { useState } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Discount } from './types/planTypes';
import { toggleDiscountInVersion } from '@/utils/discountUtils';

interface DiscountMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  discount: Discount | null;
  planId: string | null;
  planName: string | null;
  version: string | null;
  pageId: string | null;
  slug: string | null;
  isDiscountInVersion: (planId: string | null | undefined, discountId: string | null | undefined, version: string) => boolean;
  onMappingUpdated: () => void;
}

const DiscountMappingDialog: React.FC<DiscountMappingDialogProps> = ({
  open,
  onOpenChange,
  discount,
  planId,
  planName,
  version,
  pageId,
  slug,
  isDiscountInVersion,
  onMappingUpdated
}) => {
  const [loading, setLoading] = useState(false);
  
  // Always declare all hooks at the top level, regardless of conditions
  // Use useEffect to add an event listener for the Escape key
  React.useEffect(() => {
    // Only add the listener when the dialog is open
    if (!open) return;
    
    // Handler to capture and prevent default Escape key behavior
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Prevent the default action (which might include browser back)
        e.preventDefault();
        // Close the dialog manually
        onOpenChange(false);
      }
    };
    
    // Add event listener
    document.addEventListener('keydown', handleKeyDown, true);
    
    // Cleanup on unmount or when dialog closes
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open, onOpenChange]);
  
  // Early return if missing required props - MUST be after all hook declarations
  if (!discount || !version || !planName) {
    return null;
  }
  
  // Determine if the discount is currently mapped to this version
  const isMapped = isDiscountInVersion(planId, discount.id, version);

  const handleToggleMapping = async () => {
    // Enhanced debug logging
    console.log("%c 🔍 DISCOUNT MAPPING DEBUG 🔍", "background: #222; color: #ff9800; font-size: 16px;");
    console.log("Discount mapping values:", {
      discountId: discount?.id,
      planId,
      pageId,
      slug,
      version,
      isMapped,
      tempPageId: (window as any).tempPageId,
      windowProps: Object.keys(window).filter(k => k.includes('page') || k.includes('Page'))
    });
    
    // Try to access potential page ID values from the window object for debugging
    const potentialPageIds = {
      tempPageId: (window as any).tempPageId,
      pageId: pageId,
      globalPageId: (window as any).pageId,
      activePageId: (window as any).activePageId,
    };
    console.log("Potential page IDs:", potentialPageIds);
    
    // Fall back to any available pageId from multiple sources
    const effectivePageId = pageId || 
                           (window as any).tempPageId || 
                           (window as any).pageId || 
                           (window as any).activePageId || 
                           (window as any).companyPageId || 
                           // Check for our special global value
                           (window as any).GLOBAL_PAGE_ID;
    
    if (!discount) {
      console.error("❌ Discount information is missing");
      toast.error("Discount information is missing");
      return;
    }
    
    if (!version) {
      console.error("❌ Version information is missing");
      toast.error("Version information is missing");
      return;
    }
    
    if (!slug) {
      console.error("❌ Company slug is missing");
      toast.error("Company slug is missing");
      return;
    }
    
    if (!effectivePageId) {
      console.error("❌ Page ID is missing for discount mapping", {
        fromProps: !!pageId,
        fromTemp: !!(window as any).tempPageId,
        discount: discount?.id,
        slug,
        version
      });
      toast.error("Page ID is missing - please try viewing a screenshot first, then try again");
      return;
    }

    setLoading(true);
    
    try {
      // Log the page ID for debugging
      console.log("PageId debug:", { 
        directPageId: pageId, 
        tempPageId: (window as any).tempPageId 
      });
      
      // Call the API to toggle the discount's presence in this version
      const result = await toggleDiscountInVersion(
        discount.id,
        slug,
        version,
        effectivePageId
      );
      
      if (result.success) {
        if (result.added) {
          toast.success("Discount added to version successfully");
        } else {
          toast.success("Discount removed from version successfully");
        }
        onMappingUpdated();
        onOpenChange(false);
      } else {
        toast.error(result.error || "Failed to update discount mapping");
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
      console.error("Error toggling discount mapping:", error);
    } finally {
      setLoading(false);
    }
  };

  // Format discount amount based on amount_type
  const formattedAmount = discount.amount_type === 'percent' 
    ? `${discount.amount}%` 
    : discount.amount ? `$${discount.amount}` : '';

  return (
    <Dialog 
      open={open} 
      onOpenChange={onOpenChange}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Discount Mapping</DialogTitle>
          <DialogDescription>
            Update whether this discount appears in version {version}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Plan</p>
                <p className="text-sm">{planName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Version</p>
                <p className="text-sm">{version}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <div className="flex items-center gap-1 text-sm">
                  {isMapped ? (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Mapped</span>
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 text-red-600" />
                      <span>Not mapped</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Discount Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Type</p>
                  <p className="text-sm">{discount.type || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Amount</p>
                  <p className="text-sm">{formattedAmount || 'Not specified'}</p>
                </div>
                {discount.duration && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Duration</p>
                    <p className="text-sm">{discount.duration}</p>
                  </div>
                )}
                {discount.description && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Description</p>
                    <p className="text-sm">{discount.description}</p>
                  </div>
                )}
                {discount.applicable_charge_types && discount.applicable_charge_types.length > 0 && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Applies to</p>
                    <p className="text-sm">{discount.applicable_charge_types.join(', ')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleToggleMapping}
            disabled={loading}
            variant={isMapped ? "destructive" : "default"}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : isMapped ? (
              <X className="h-4 w-4 mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {isMapped ? "Remove from Version" : "Add to Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DiscountMappingDialog;
